import { useTelegramChannelsSearchFilters } from './components/usePageParams';
import { GetTelegramChannelsParams } from 'api/types/httpsTypes/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
} from 'api/services/httpServices/ContractService';
import { duration } from 'moment';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import Table from 'modules/shared-components/data-display/table';
import Header from 'modules/shared-components/data-display/table/header';
import { EmptyPageDex, ErrorPageDex } from '../shared/Common';
import { TelegramChannelsFiltersBar } from './components/TelegramChannelsFilters';
import { desktopColumns, mobileColumns } from './components/columns';
import { TelegramChannelsRow } from './components/TelegramChannelsRow';
import { useRef } from 'react';
import { Pagination } from 'modules/shared-components/pagination';

export function thDynamicClass(s: string) {
  return s === 'CHANNEL' ? 'col-span-2' : '';
}

export default function TelegramChannels() {
  const { goToPage, filtersState, setSort } =
    useTelegramChannelsSearchFilters();

  const params: GetTelegramChannelsParams = {
    limit: filtersState.limit,
    offset: filtersState.offset,
    sort: filtersState.sort,
    timePeriodInDays: filtersState.timePeriodInDays,
    minNumberOfCalls: filtersState.minNumberOfCalls,
    maxNumberOfCalls: filtersState.maxNumberOfCalls,
    minRugPulls: filtersState.minRugPulls,
    maxRugPulls: filtersState.maxRugPulls,
    minWinnersVsLosers: filtersState.minWinnersVsLosers,
    maxWinnersVsLosers: filtersState.maxWinnersVsLosers,
    search: filtersState.search,
  };

  const listRef = useRef<HTMLTableElement>(null);

  function scrollAndNavigate(pageNumber: number) {
    goToPage(pageNumber);
    if (listRef.current) {
      listRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  const isSortAscending = !filtersState.sort?.includes('-');

  const {
    data: page,
    error: error,
    isLoading,
  } = useQuery({
    queryKey: contractServiceKeys.getTelegramChannels(params),
    queryFn: () => ContractService.getTelegramChannels(params),
    keepPreviousData: true,
    refetchInterval: duration(12, 'seconds').asMilliseconds(),
  });

  const isLargeScreen = useMediaQuery(screenGte.large);
  const columns = isLargeScreen ? desktopColumns : mobileColumns;

  return (
    <div className="space-y-2 lg:px-2">
      <div className="xxs:px-2 lg:px-0 space-y-1">
        <TelegramChannelsFiltersBar />
      </div>

      {error ? (
        <ErrorPageDex />
      ) : (
        <>
          <div className="overflow-x-auto">
            <div className="w-full  xxs:my-3 lg:min-w-[1000px] ">
              {!isLargeScreen && <div ref={listRef} />}
              <Table className="xxs:[&_td:first-of-type:not(.details)]:pl-2 xxs:[&_th:first-of-type]:pl-2 lg:[&_td:first-of-type:not(.details)]:pl-6 lg:[&_th:first-of-type]:pl-6">
                <Header
                  trClassName="lg:grid lg:grid-cols-8 lg:dark:bg-dex-black-900  border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-b-dex-black-700"
                  columns={columns}
                  thDynamicClass={thDynamicClass}
                  rowClassName="xxs:p-1"
                  isSortAscending={isSortAscending}
                  sortBy={filtersState.sort?.replace('-', '')}
                  className="lg:dark:bg-dex-black-900 xxs:text-xs lg:grid"
                  onClickColumn={(_, sortableKey) => {
                    if (sortableKey && !isLoading) setSort(sortableKey);
                  }}
                />
                <tbody className="lg:block lg:h-terminal overflow-y-auto">
                  {isLoading ? (
                    [...Array(10).keys()]
                      .map((i) => i + 1)
                      .map((index) => (
                        <Table.LoadingRow
                          thDynamicClass={thDynamicClass}
                          key={index}
                          elementClassName="xxs:dark:bg-dex-black-700"
                          className="lg:grid lg:grid-cols-8 lg:dark:bg-dex-black-900"
                          columns={columns}
                        />
                      ))
                  ) : page?.data?.length === 0 ? (
                    <EmptyPageDex
                      height={isLargeScreen ? '100px' : '50vh'}
                      title="No Telegram Channels Found"
                    />
                  ) : (
                    page?.data?.map((item, rowId) => (
                      <TelegramChannelsRow
                        key={rowId}
                        data={item}
                        columnsDef={columns}
                      />
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          {page && page?.data?.length > 0 && (
            <div className="xxs:px-2 lg:px-0">
              <Pagination
                goToPage={scrollAndNavigate}
                activePage={page.pagination}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
